import { CONSTANTS_VALUE } from '../../config/constants.module';
import { WhereClauseUtil } from './where-clause-util';
import { AgGridFilterType } from '../enums/ag-grid-filter-type.enum';

export class ColumnFilterUtil {
  public static createWhereSql(filterModel: any) {
    const whereParts = [];

    if (filterModel) {
      const keySet = Object.keys(filterModel);
      keySet.forEach(function (key) {
        const item = filterModel[key];
        const attributeName = ColumnFilterUtil.removeSubfix(key);
        const part = ColumnFilterUtil.createFilterSql(attributeName, item);
        if (part.length > 0) {
          whereParts.push(part);
        }
      });
    }

    return WhereClauseUtil.joinWhere(whereParts);
  }

  /* Removes subfix '_N' added by agGrid when
  using duplicated columns*/
  private static removeSubfix(key: string) {
    return key.replace(/_\d+$/, '');
  }

  private static createFilterSql(key, item): string {
    if (item.operator) {
      return ColumnFilterUtil.createOperatorFilterSql(key, item);
    } else if (item.filterType) {
      return ColumnFilterUtil.createTypeFilterSql(key, item);
    } else {
      console.log(`unknown text filter type: ${JSON.stringify(item)}`);
      return '1=1';
    }
  }

  private static createTypeFilterSql(key, item): string {
    switch (item.filterType) {
      case AgGridFilterType.sql:
        return `${item.filter}`;
      case AgGridFilterType.text:
        return ColumnFilterUtil.createTextTypeFilterSql(key, item);
      case AgGridFilterType.number:
        return ColumnFilterUtil.createNumberTypeFilterSql(key, item);
      case AgGridFilterType.set:
        return ColumnFilterUtil.createSetTypeFilterSql(key, item);
      case AgGridFilterType.date:
        return ColumnFilterUtil.createDateTypeFilterSql(key, item);
      default:
        console.log(`unkonwn filter type: ${item.filterType}`);
        throw new Error(`filterType unkown: ${item.filterType}`);
    }
  }

  private static createOperatorFilterSql(key, item): string {
    const condition1Where = ColumnFilterUtil.createTypeFilterSql(key, item.condition1);
    const condition2Where = ColumnFilterUtil.createTypeFilterSql(key, item.condition2);

    switch (item.operator.toLowerCase()) {
      case 'and':
        return `(${condition1Where} AND ${condition2Where})`;
      case 'or':
        return `(${condition1Where} OR ${condition2Where})`;
      default:
        console.log(`unknown text filter operator: ${item.operator}`);
        return '1=1';
    }
  }

  private static createNumberTypeFilterSql(key, item): string {
    switch (item.type) {
      case 'blanks':
        return `${key} IS NULL`;
      case 'noBlanks':
        return `${key} IS NOT NULL`;
      case 'equals':
        return `${key} = ${item.filter}`;
      case 'notEqual':
        return `${key} != ${item.filter}`;
      case 'greaterThan':
        return `${key} > ${item.filter}`;
      case 'greaterThanOrEqual':
        return `${key} >= ${item.filter}`;
      case 'lessThan':
        return `${key} < ${item.filter}`;
      case 'lessThanOrEqual':
        return `${key} <= ${item.filter}`;
      case 'inRange':
        return `(${key} >= ${item.filter} and ${key} <= ${item.filterTo})`;
      default:
        console.log('unknown number filter type: ' + item.type);
        return '1=1';
    }
  }

  private static createTextTypeFilterSql(key, item): string {
    switch (item.type) {
      case 'blanks':
        return `${key} IS NULL`;
      case 'noBlanks':
        return `${key} IS NOT NULL`;
      case 'equals':
        return `${key} = '${item.filter}'`;
      case 'notEqual':
        return `${key} != '${item.filter}'`;
      case 'contains':
        return `lower(${key}) like lower('%${item.filter}%')`;
      case 'notContains':
        return `lower(${key}) not like lower('%${item.filter}%')`;
      case 'startsWith':
        return `${key} like '${item.filter}%'`;
      case 'endsWith':
        return `${key} like '%${item.filter}'`;
      default:
        console.log(`unknown text filter type: ${item.type}`);
        return '1=1';
    }
  }

  private static createSetTypeFilterSql(key, item): string {
    const values = item.values;

    // avoid empty IN()
    if (!values) return '';
    // no filter set
    else if (values.length === 0) return `${key} <> ${key}`; // no record returned

    const preCondition = item.orNull === true ? `${key} IS NULL OR ` : ``;
    return `${preCondition}${WhereClauseUtil.getInOrNullClause(key, values, CONSTANTS_VALUE.agGrid.emptyFilterValue)}`;
  }

  private static createDateTypeFilterSql(key, item): string {
    const dateFrom = this.convertDateToSql(item.dateFrom);
    switch (item.type) {
      case 'blank':
        return `${key} IS NULL`;
      case 'notBlank':
        return `${key} IS NOT NULL`;
      case 'equals':
        return `${key} = timestamp '${dateFrom}'`;
      case 'notEqual':
        return `${key} <> timestamp '${dateFrom}'`;
      case 'greaterThan':
        return `${key} > timestamp '${dateFrom}'`;
      case 'greaterThanOrEqual':
        return `${key} >= timestamp '${dateFrom}'`;
      case 'lessThan':
        return `${key} < timestamp '${dateFrom}'`;
      case 'lessThanOrEqual':
        return `${key} <= timestamp '${dateFrom}'`;
      case 'inRange':
        return `(${key} >= timestamp '${dateFrom}' and ${key} <= timestamp '${this.convertDateToSql(item.dateTo)}')`;
      default:
        console.log('unknown date filter type: ' + item.type);
        return '1=1';
    }
  }

  private static convertDateToSql(dateStr: string): string {
    if (!dateStr) {
      return '';
    }
    return dateStr.split(' ')[0]; //YYYY-MM-DD
  }
}
